/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from "react";
import { notesApi } from "../api";
import { FullNote } from "../interfaces/mortgageNote";
import { faArrowLeft, faLocationDot } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropertyImageCarousel from "../components/note/PropertyImageCarousel";
import NoteInfoCard from "../components/note/NoteInfoCard";
import { Highlight } from "../interfaces/mortgageNote";
import addCommas from "../helpers/addCommas";
import { calculateMonthlyReturn } from "../helpers/calculateMonthyReturn";
import { useNavigate } from "react-router-dom";
import ActionFormWrapper from "../components/note/ActionFormWrapper";
import RequestInformationForm from "../components/note/RequestInformationForm";
import NoteSoldForm from "../components/note/NoteSoldForm";
import useAuth from "../hooks/useAuth";
import AdminToolbar from "../components/admin/AdminToolbar";
import useUserAuth from "../hooks/useUserAuth";
import SavedNoteButton from "../components/buttons/SavedNoteButton";
import LoadingTable from "../components/loading/LoadingTable";
// import useModal from "../hooks/useModal";
// import DisclosureModal from "../components/modals/DisclosureModal";
// import DownloadLoanPackageModal from "../components/modals/DownloadLoanPackageModal";

interface NoteProps {
  noteID: number;
}

const Note = ({ noteID }: NoteProps) => {
  const { currentAdmin } = useAuth();

  const { currentUser, isLoading } = useUserAuth();

  const [note, setNote] = useState<FullNote>();

  const navigate = useNavigate();

  const [isFetchingNote, setIsFetchingNote] = useState<boolean>(true);

  // const { openModalWith } = useModal();

  const fetchNote = async () => {
    if (isLoading) return;
    try {
      setIsFetchingNote(true);
      const note = await notesApi.fetchNote(noteID, currentUser?.id);
      setNote(note);
    } catch (err: any) {
      console.error(err);
    } finally {
      setIsFetchingNote(false);
    }
  };

  useEffect(() => {
    fetchNote();
  }, [noteID, isLoading]);

  if (!note) {
    return <></>;
  }

  const {
    street_number,
    city,
    state,
    postal_code,
    loan_type,
    loan_amount,
    loan_term,
    is_performing,
    appraised_value,
    loan_to_value,
    purchase_price,
    interest_rate,
    down_payment,
    lien_position,
    funds_held_in_escrow,
    type,
    exit_strategy,
    use_of_proceeds,
    maturity_date,
    average_credit,
    average_balance,
    repeat_borrower,
    previous_cogo,
    bedrooms,
    bathrooms,
    sqrfeet,
    highlights,
    loan_package_pdf,
  } = note;

  const buildAddress = (): string =>
    `${street_number} ${city}, ${state} ${postal_code}`;

  // const handleDisclosureClick = () => {
  //   openModalWith({
  //     title: "Non-Disclosure & Non-Circumvent Agreement",
  //     body: <DisclosureModal />,
  //     hideButtons: true,
  //   });
  // };

  // const handleDownloadClick = () => {
  //   openModalWith({
  //     title: "",
  //     body: <DownloadLoanPackageModal note={note} />,
  //     hideButtons: true,
  //   });
  // };

  const formatDate = (dateString: Date | string): string => {
    const date =
      typeof dateString === "string" ? new Date(dateString) : dateString;

    // Use Intl.DateTimeFormat for consistent formatting
    const formatter = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "UTC", // Ensures no local time conversion
    });

    return formatter.format(date);
  };

  const overviewRows = [
    { label: "LOAN TYPE", value: loan_type },
    { label: "LIEN POSITION", value: lien_position },
    { label: "PERFORMING", value: is_performing ? "Yes" : "No" },
    { label: "LOAN AMOUNT", value: `$${addCommas(loan_amount)}`, loan_amount },
    { label: "PURCHASE PRICE", value: `$${addCommas(purchase_price || 0)}` },
    { label: "MARKET VALUE", value: `$${addCommas(appraised_value || 0)}` },
    {
      label: "DOWN PAYMENT",
      value: `$${addCommas(down_payment || 0)}`,
      down_payment,
    },
    { label: "ARV LTV", value: `${loan_to_value}%` },
    { label: "LOAN TERM", value: `${loan_term} Months` },
    {
      label: "TARGETED MONTHLY RETURN",
      value: `$${calculateMonthlyReturn(
        loan_amount,
        interest_rate,
        loan_term
      )}`,
    },
    {
      label: "AMOUNT HELD FOR REHAB",
      value: `$${addCommas(funds_held_in_escrow || 0)}`,
    },
    { label: "USE OF PROCEEDS", value: use_of_proceeds },
    { label: "EXIT STRATEGY", value: exit_strategy },
    { label: "MATURITY DATE", value: formatDate(maturity_date) },
    {
      label: "TARGETED RATES OF RETURN",
      value: `${interest_rate ? `${interest_rate}% ` : "-"}`,
    },
    { label: "AVERAGE CREDIT", value: average_credit ?? "-" },
    { label: "AVERAGE BANK BALANCE", value: `$${average_balance ?? 0}` },
    { label: "REPEAT BORROWER", value: repeat_borrower ? "Yes" : "No" },
    {
      label: "PREVIOUS LOAN WITH COGO CAPITAL",
      value: previous_cogo ? "Yes" : "No",
    },
  ];

  const addressRows = [
    { label: "ADDRESS", value: buildAddress() },
    { label: "TYPE", value: type },
    { label: "BEDROOMS", value: bedrooms },
    { label: "BATHROOMS", value: bathrooms },
    { label: "SQUARE FOOTAGE", value: addCommas(sqrfeet || 0) },
  ];

  if (isFetchingNote) {
    return (
      <div className="h-full w-screen flex flex-col items-center justify-center pt-12 pb-24">
        <LoadingTable label="Note" />
      </div>
    );
  }

  return (
    <div className="h-full w-screen px-4 text-xs xs:text-sm md:text-base flex flex-col items-center justify-center">
      <div className="w-screen flex flex-col items-center justify-center mb-12 xl:max-w-[1320px] px-8 xl:pr-0">
        <div className="w-full flex flex-col md:flex-row sm:gap-4 items-start justify-center">
          <div className="w-full md:w-7/12 ">
            <div className="text-black flex gap-1.5 items-center justify-start text-2xl font-semibold mb-3 ">
              <FontAwesomeIcon icon={faLocationDot} className="" />
              <span>{buildAddress()}</span>
            </div>
            <div className="mb-3 lg:w-full">
              <PropertyImageCarousel images={note.images} />
            </div>
            {currentAdmin && (
              <div className="py-2 pb-4">
                <AdminToolbar note={note} />
              </div>
            )}
            {note.status === "Available" && loan_package_pdf && (
              <div>
                {/* <div className="text-black flex gap-1.5 items-center justify-start text-2xl font-bold mb-3">
                  <span>VIEW LOAN PACKAGE</span>
                </div>
                <div className="text-xs text-main-gray mb-3">
                  <span>
                    I would like to download the loan package, and agree to the
                    following
                    <span
                      className="font-medium hover:underline text-blue-700 cursor-pointer"
                      onClick={handleDisclosureClick}
                    >
                      {" "}
                      Non-Disclosure & Non-Circumvent Agreement.
                    </span>
                  </span>
                </div> */}
                <div className="flex flex-row gap-2 items-center justify-between mb-5">
                  {/* <div className="w-fit">
                    <ActionButton
                      onClick={handleDownloadClick}
                      label={"DOWNLOAD LOAN PACKAGE"}
                    />
                  </div> */}
                  {currentUser && <SavedNoteButton note={note} />}
                </div>
              </div>
            )}
            <div className="mb-3">
              <NoteInfoCard header={"INVESTMENT SUMMARY"}>
                <div className="w-full">
                  <table className="w-full mt-3">
                    <thead className="border-b">
                      <tr>
                        <td className="text-main-gray font-bold py-2 px-1">
                          TYPE
                        </td>
                        <td className="text-main-gray font-bold py-2 px-1">
                          UNDERLYING ASSET
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-gray-100">
                        <td className="text-main-gray font-medium py-2 px-1">
                          {loan_type}
                        </td>
                        <td className="text-main-gray font-medium py-2 px-1">
                          {type}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="px-4">
                  <table className="w-full mt-3 border ">
                    <tbody>
                      {highlights.map((highlight: Highlight) => (
                        <tr key={highlight.id} className="border-b">
                          <td className="text-main-gray text-sm font-medium px-2 py-2">
                            {highlight.name}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </NoteInfoCard>
            </div>
            <div className="mb-3">
              <NoteInfoCard header={"OVERVIEW"}>
                <div className="w-full">
                  <table className="w-full mt-3 border">
                    <tbody>
                      {overviewRows.map((row, index) => (
                        <tr key={index} className="border-b">
                          <td className="text-main-gray font-medium py-2 px-1 border-r pl-3">
                            {row.label}
                          </td>
                          <td className="text-main-gray font-medium py-2 pl-3">
                            {row.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </NoteInfoCard>
            </div>
            <div className="mb-3">
              <NoteInfoCard header={"ABOUT THE PROPERTY"}>
                <div className="w-full pt-4">
                  <div className="">
                    <iframe
                      title="Property Map"
                      width="100%"
                      height="300px"
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(
                        buildAddress()
                      )}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
                    ></iframe>
                  </div>
                  <div className="font-bold text-main-gray my-2">Details</div>
                  <table className="w-full mt-1 border">
                    <tbody>
                      {addressRows.map((row, index) => (
                        <tr key={index} className="border-b">
                          <td className="text-main-gray font-medium py-2 px-1 border-r pl-3">
                            {row.label}
                          </td>
                          <td className="text-main-gray font-medium py-2 pl-3">
                            {row.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </NoteInfoCard>
            </div>
            <div className="mt-4">
              <div
                className="flex justify-start items-center gap-2 cursor-pointer"
                onClick={() => {
                  navigate(-1);
                  setTimeout(() => {
                    window.scrollTo(0, 820);
                  }, 50);
                }}
              >
                <FontAwesomeIcon
                  icon={faArrowLeft}
                  className="text-main-gray text-xl"
                />
                <span className="text-main-gray font-bold text-lg">
                  BACK TO NOTES
                </span>
              </div>
            </div>
          </div>
          {!currentUser && (
            <div className="w-full md:w-5/12 relative ">
              <div className="md:absolute -top-24 left-0">
                {note.status === "Available" ? (
                  <ActionFormWrapper
                    title={"REQUEST INFORMATION"}
                    hook={
                      <span className="italic">
                        THIS NOTE IS
                        <span className="font-bold"> AVAILABLE </span>
                        FOR PURCHASE
                      </span>
                    }
                  >
                    <RequestInformationForm note={note} />
                  </ActionFormWrapper>
                ) : (
                  <ActionFormWrapper
                    title={"SOLD!"}
                    hook={
                      <span className="italic">
                        THIS NOTE IS
                        <span className="font-bold"> NOT AVAILABLE </span>
                        FOR PURCHASE
                      </span>
                    }
                  >
                    <NoteSoldForm />
                  </ActionFormWrapper>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Note;
